import { useState, type KeyboardEvent } from "react"
import clsx from "clsx"
import {
  CheckCircleIcon,
  ChevronUpDownIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  UserIcon,
  XCircleIcon,
} from "@heroicons/react/20/solid"
import Tooltip from "@/components/Tooltip"
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react"
import { mergeSelectedRecord } from "@/utils/mergeSelectedRecord"
import {
  filterRecordsByName,
  findExactNameMatch,
  hasExactNameMatch,
  normalizeRecordName,
} from "@/utils/creatableRecords"

type NamedRecord = {
  id: string
  name: string
  is_chessdrop_user?: boolean
}

type CreateOption = {
  id: "__create__"
  name: string
}

type Selection<T extends NamedRecord> = T | CreateOption

const CREATE_OPTION_ID = "__create__"

const isCreateOption = <T extends NamedRecord>(
  option: Selection<T> | null
): option is CreateOption => option?.id === CREATE_OPTION_ID

const CreatableRecordSelector = <T extends NamedRecord>({
  createActionLabel,
  emptyActionLabel,
  inputId,
  label,
  labelContent,
  maxHeightClassName = "max-h-[235px]",
  recordId,
  records,
  selectedRecordFallback,
  onBlur,
  onChange,
  onCreateAsync,
  onQueryChange,
  pinnedRecordId,
  pinnedRecordLabel,
}: {
  createActionLabel: string
  emptyActionLabel: string
  inputId: string
  label: string
  labelContent?: React.ReactNode
  maxHeightClassName?: string
  recordId?: string
  records: T[]
  selectedRecordFallback?: T | null
  onBlur: () => void
  onChange: (value: string | null) => void
  onCreateAsync: (data: { name: string }) => Promise<T>
  onQueryChange?: (query: string) => void
  pinnedRecordId?: string | null
  pinnedRecordLabel?: string
}) => {
  const [query, setQuery] = useState("")
  const [isCreating, setIsCreating] = useState(false)

  const resetQuery = () => {
    setQuery("")
    onQueryChange?.("")
  }

  const availableRecords = mergeSelectedRecord(records, selectedRecordFallback)
  const selectedRecord =
    availableRecords.find((record) => record.id === recordId) ?? null
  const allFilteredRecords = filterRecordsByName(availableRecords, query)

  // Separate pinned record from the rest so it always appears first
  const pinnedRecord = pinnedRecordId
    ? allFilteredRecords.find((r) => r.id === pinnedRecordId)
    : null
  const filteredRecords = pinnedRecord
    ? allFilteredRecords.filter((r) => r.id !== pinnedRecordId)
    : allFilteredRecords
  const trimmedQuery = query.trim()
  const hasQuery = trimmedQuery.length > 0
  const exactMatchRecord = findExactNameMatch(availableRecords, trimmedQuery)
  const showCreateOption =
    hasQuery && !hasExactNameMatch(availableRecords, trimmedQuery)
  const createOption = showCreateOption
    ? ({ id: CREATE_OPTION_ID, name: trimmedQuery } satisfies CreateOption)
    : null

  const handleSelection = async (selection: Selection<T> | null) => {
    if (!selection) {
      onChange(null)
      onBlur()
      resetQuery()
      return
    }

    if (isCreateOption(selection)) {
      try {
        setIsCreating(true)
        const newRecord = await onCreateAsync({ name: selection.name })
        onChange(newRecord.id)
      } finally {
        setIsCreating(false)
      }

      onBlur()
      resetQuery()
      return
    }

    onChange(selection.id)
    onBlur()
    resetQuery()
  }

  const emptyStateMessage = hasQuery
    ? `No ${emptyActionLabel} found.`
    : `Type to search or create ${emptyActionLabel}.`

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return
    if (!trimmedQuery) return
    if (event.currentTarget.getAttribute("aria-activedescendant")) return

    event.preventDefault()

    if (exactMatchRecord) {
      void handleSelection(exactMatchRecord)
      return
    }

    if (createOption) {
      void handleSelection(createOption)
    }
  }

  return (
    <Combobox
      as="div"
      value={selectedRecord}
      onChange={(selection) => void handleSelection(selection)}
      onClose={resetQuery}
      nullable
      disabled={isCreating}
      className="relative"
    >
      <label
        htmlFor={inputId}
        className="block text-sm leading-6 font-medium text-gray-900"
      >
        {labelContent ?? label}
      </label>

      <div className="relative mt-1">
        <ComboboxInput
          id={inputId}
          autoComplete="off"
          className="focus:ring-chessdrop-600 block w-full rounded-lg border-0 py-2 pr-16 pl-3 text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
          displayValue={(record: T | null) =>
            record
              ? pinnedRecordId && record.id === pinnedRecordId && pinnedRecordLabel
                ? `${record.name} (${pinnedRecordLabel})`
                : record.name
              : ""
          }
          onChange={(event) => {
            let value = event.target.value
            // Only strip the "(Label)" suffix when editing from the pinned record's display value
            if (pinnedRecordLabel && selectedRecord && pinnedRecordId && selectedRecord.id === pinnedRecordId) {
              const suffix = ` (${pinnedRecordLabel})`
              for (let i = suffix.length; i > 0; i--) {
                if (value.endsWith(suffix.slice(0, i))) {
                  value = value.slice(0, -i)
                  break
                }
              }
            }
            setQuery(value)
            onQueryChange?.(value)
          }}
          onKeyDown={handleInputKeyDown}
          placeholder={`Type to search or create ${emptyActionLabel}`}
        />

        <div className="absolute inset-y-0 right-0 flex items-center gap-1 pr-1">
          {recordId ? (
            <button
              type="button"
              onClick={() => void handleSelection(null)}
              className="group rounded-md px-1.5 py-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={isCreating}
              aria-label={`Clear ${label.toLowerCase()}`}
            >
              <XCircleIcon className="size-5 text-gray-400 group-hover:text-gray-500" />
            </button>
          ) : null}

          <ComboboxButton className="rounded-md px-1.5 py-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none">
            <ChevronUpDownIcon className="size-5" aria-hidden="true" />
          </ComboboxButton>
        </div>

        <ComboboxOptions
          className={clsx(
            "absolute z-10 mt-1 w-full overflow-auto rounded-lg bg-white shadow-lg ring-1 ring-black/5 empty:invisible focus:outline-none sm:text-sm",
            maxHeightClassName
          )}
        >
          {showCreateOption ? (
            <ComboboxOption
              value={createOption}
              disabled={isCreating}
              className="group border-b border-gray-100 px-3 py-2 text-left text-gray-900 select-none data-disabled:cursor-wait data-disabled:opacity-50 data-focus:bg-gray-100"
            >
              <div className="flex items-center gap-2">
                <PlusIcon className="size-4 text-gray-400 group-data-focus:text-gray-500" />
                <span className="truncate">
                  {createActionLabel} &ldquo;{trimmedQuery}&rdquo;
                </span>
              </div>
            </ComboboxOption>
          ) : null}

          {pinnedRecord ? (() => {
            const selected = pinnedRecord.id === recordId
            return (
              <ComboboxOption
                key={pinnedRecord.id}
                value={pinnedRecord}
                className="relative border-b border-gray-100 px-3 py-2 text-left text-gray-900 select-none data-focus:bg-gray-100"
              >
                <div className="flex items-center gap-2">
                  {selected ? (
                    <CheckCircleIcon
                      className="text-chessdrop-600 size-5"
                      aria-hidden="true"
                    />
                  ) : (
                    <UserIcon
                      className="text-chessdrop-600 size-5 shrink-0"
                      aria-hidden="true"
                    />
                  )}
                  <span
                    className={clsx(
                      "block truncate",
                      selected && "font-semibold"
                    )}
                  >
                    {pinnedRecord.name}
                    {pinnedRecordLabel ? (
                      <span className="ml-1.5 text-xs text-gray-400">
                        ({pinnedRecordLabel})
                      </span>
                    ) : null}
                  </span>
                </div>
              </ComboboxOption>
            )
          })() : null}

          {filteredRecords.map((record) => {
            const selected = record.id === recordId
            const normalizedQuery = normalizeRecordName(query)
            const startsWithQuery =
              normalizedQuery.length > 0 &&
              normalizeRecordName(record.name).startsWith(normalizedQuery)

            return (
              <ComboboxOption
                key={record.id}
                value={record}
                className="relative px-3 py-2 text-left text-gray-900 select-none data-focus:bg-gray-100"
              >
                <div className="flex items-center gap-2">
                  {selected ? (
                    <CheckCircleIcon
                      className="text-chessdrop-600 size-5"
                      aria-hidden="true"
                    />
                  ) : record.is_chessdrop_user ? (
                    <Tooltip text="ChessDrop member">
                      <UserIcon className="text-chessdrop-600 size-5 shrink-0" />
                    </Tooltip>
                  ) : (
                    <span className="size-5 shrink-0" aria-hidden="true" />
                  )}
                  <span
                    className={clsx(
                      "block truncate",
                      selected && "font-semibold",
                      startsWithQuery && "font-medium"
                    )}
                  >
                    {record.name}
                  </span>
                </div>
              </ComboboxOption>
            )
          })}

          {filteredRecords.length === 0 && !pinnedRecord && !showCreateOption ? (
            <div className="px-3 py-6 text-center text-sm text-gray-500">
              <div className="mb-2 flex justify-center">
                <MagnifyingGlassIcon className="size-4 text-gray-400" />
              </div>
              {emptyStateMessage}
            </div>
          ) : null}
        </ComboboxOptions>
      </div>
    </Combobox>
  )
}

export default CreatableRecordSelector
